import React from 'react';
import { Chip, Stack, Tooltip } from '@mui/material';

/**
 * Color + label conventions for the two status fields. Tutor-side
 * engagement labels swap (Teaching / Not teaching) per architecture doc;
 * stored enum values are the same.
 */
const ACCOUNT_CHIP = {
    pending_approval: { label: 'Pending approval', color: 'warning' },
    approved: { label: 'Approved', color: 'success' },
    declined: { label: 'Declined', color: 'error' },
    archived: { label: 'Archived', color: 'default' },
};

const engagementChip = (role) => ({
    enrolled: {
        label: role === 'teacher' ? 'Teaching' : 'Enrolled',
        color: 'primary',
    },
    not_enrolled: {
        label: role === 'teacher' ? 'Not teaching' : 'Not enrolled',
        color: 'default',
    },
    on_break: { label: 'On break', color: 'info' },
});

/**
 * Renders one or both status chips for an account. Pass the account
 * object (must include role, accountStatus, engagementStatus).
 *
 * Props:
 *   account               required
 *   show     'both' | 'account' | 'engagement'   default 'both'
 *   size     'small' | 'medium'                 default 'small'
 *   gap                                          default 0.5
 */
const AccountStatusChips = ({ account, show = 'both', size = 'small', gap = 0.5 }) => {
    if (!account) return null;
    const role = account.role;
    const acc = ACCOUNT_CHIP[account.accountStatus] || {
        label: account.accountStatus || '—',
        color: 'default',
    };
    const eng = engagementChip(role)[account.engagementStatus] || {
        label: account.engagementStatus || '—',
        color: 'default',
    };

    const chips = [];
    if (show === 'both' || show === 'account') {
        chips.push(
            <Tooltip key="acc" title="Account status (set by admin during approval)">
                <Chip size={size} label={acc.label} color={acc.color} variant="outlined" />
            </Tooltip>
        );
    }
    if (show === 'both' || show === 'engagement') {
        chips.push(
            <Tooltip key="eng" title="Engagement — whether they have a current class">
                <Chip size={size} label={eng.label} color={eng.color} />
            </Tooltip>
        );
    }

    return (
        <Stack direction="row" spacing={gap} sx={{ flexWrap: 'wrap', gap }}>
            {chips}
        </Stack>
    );
};

export default AccountStatusChips;
export { ACCOUNT_CHIP, engagementChip };
